export type EstateAvailability =
  | "available"
  | "reserved"
  | "sold"
  | "rented"
  | null;
export type FlatfinderPoint = { x: number; y: number };
export type FlatfinderRegion = {
  id: string;
  estateId: string | null;
  points: FlatfinderPoint[];
  color: string;
  sortOrder: number;
};
export type FlatfinderPerspective = {
  id: string;
  imageId: string;
  mediaId: string;
  width: number;
  height: number;
  name: string;
  description: string;
  sortOrder: number;
  regions: FlatfinderRegion[];
};
export type FlatfinderEstateInput = {
  name: string;
  topNumber: string | null;
  availability: EstateAvailability;
  marketType: "buy" | "rent" | "lease";
  currency: string;
  price: number | null;
  livingArea: number | null;
  rooms: number | null;
  floor: number | null;
  description: string | null;
  digitalTwinLinkToken: string | null;
  externalLink: string | null;
  buildingId: string | null;
};
export type FlatfinderEstate = FlatfinderEstateInput & {
  id: string;
  status: string;
  usableArea: number | null;
  balconyArea: number | null;
  gardenArea: number | null;
  terraceArea: number | null;
  bedrooms: number | null;
  bathrooms: number | null;
  parkingSpaces: number | null;
  accessibility: boolean | null;
  constructionYear: string | null;
  canEdit: boolean;
  canPublish: boolean;
  floorPlans: {
    id: string;
    mediaId: string;
    name: string;
    fileType: string | null;
  }[];
};
export type FlatfinderData = {
  id: string | null;
  projectId: string;
  publicId: string | null;
  status: "draft" | "published";
  contactLink: string | null;
  revision: number;
  project: {
    name: string;
    status: string;
    marketType: "buy" | "rent" | "lease";
    legalNoticeLink: string | null;
    privacyPolicyLink: string | null;
    buildings: { id: string; name: string }[];
  };
  branding: {
    name: string;
    logoMediaId: string | null;
    primaryColor: string | null;
  } | null;
  permissions: { general: boolean; media: boolean };
  perspectives: FlatfinderPerspective[];
  estates: FlatfinderEstate[];
};

export function isEstateAvailabilityCompatible(
  availability: EstateAvailability,
  marketType: string,
) {
  return (
    (availability !== "sold" && availability !== "rented") ||
    (availability === "sold" && marketType === "buy") ||
    (availability === "rented" &&
      (marketType === "rent" || marketType === "lease"))
  );
}

export function isValidFlatfinderPolygon(points: FlatfinderPoint[]) {
  if (
    points.length < 3 ||
    points.length > 200 ||
    points.some(
      ({ x, y }) =>
        !Number.isFinite(x) ||
        !Number.isFinite(y) ||
        x < 0 ||
        x > 1 ||
        y < 0 ||
        y > 1,
    )
  )
    return false;
  const cross = (a: FlatfinderPoint, b: FlatfinderPoint, c: FlatfinderPoint) =>
    (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
  const onSegment = (
    a: FlatfinderPoint,
    b: FlatfinderPoint,
    p: FlatfinderPoint,
  ) =>
    Math.abs(cross(a, b, p)) < 1e-10 &&
    p.x >= Math.min(a.x, b.x) &&
    p.x <= Math.max(a.x, b.x) &&
    p.y >= Math.min(a.y, b.y) &&
    p.y <= Math.max(a.y, b.y);
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    if (Math.hypot(a.x - b.x, a.y - b.y) < 1e-8) return false;
    area += a.x * b.y - b.x * a.y;
    for (let j = i + 1; j < points.length; j++) {
      if (j === i + 1 || (i === 0 && j === points.length - 1)) continue;
      const c = points[j];
      const d = points[(j + 1) % points.length];
      if (
        (cross(a, b, c) * cross(a, b, d) < 0 &&
          cross(c, d, a) * cross(c, d, b) < 0) ||
        onSegment(a, b, c) ||
        onSegment(a, b, d) ||
        onSegment(c, d, a) ||
        onSegment(c, d, b)
      )
        return false;
    }
  }
  return Math.abs(area) > 1e-8;
}
